import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@core/auth/useAuth'
import { authApi } from '@core/api/auth'
import { ApiError, NetworkError } from '@core/api/client'

type Status = 'idle' | 'loading' | 'submitting' | 'done' | 'error'

const MIN_LENGTH = 8

function SetPasswordPage() {
  const { user, workspace, hasPassword: hasPwFromCtx, refresh } = useAuth()
  const navigate = useNavigate()
  const [hasPassword, setHasPassword] = useState<boolean | null>(
    hasPwFromCtx,
  )
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  // Keep local mirror in sync with context updates (e.g. another tab
  // sets the password). The context already drives the banner's
  // visibility: usamo-lo aqui também para poupar um fetch extra.
  // Adotado durante o render com guarda de referência (em vez de um
  // efeito), para o espelho ficar correto logo no primeiro render.
  const [prevHasPwFromCtx, setPrevHasPwFromCtx] = useState(hasPwFromCtx)
  if (prevHasPwFromCtx !== hasPwFromCtx) {
    setPrevHasPwFromCtx(hasPwFromCtx)
    setHasPassword(hasPwFromCtx)
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setErrorMsg('')
    if (newPassword.length < MIN_LENGTH) {
      setErrorMsg(`Password tem de ter pelo menos ${MIN_LENGTH} caracteres.`)
      return
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('As passwords não coincidem.')
      return
    }
    if (hasPassword && !currentPassword) {
      setErrorMsg('Indica a password atual.')
      return
    }
    setStatus('submitting')
    try {
      await authApi.setPassword(
        newPassword,
        hasPassword ? currentPassword : null,
      )
      setStatus('done')
      setHasPassword(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      // Propagate to global state so the banner disappears.
      void refresh()
    } catch (err) {
      setStatus('error')
      if (err instanceof NetworkError) {
        setErrorMsg('Não foi possível contactar o servidor.')
      } else if (err instanceof ApiError) {
        const detail =
          (err.body as { detail?: string } | null)?.detail ?? null
        setErrorMsg(detail ?? `Pedido recusado pelo servidor (${err.status}).`)
      } else {
        setErrorMsg('Erro inesperado.')
      }
    }
  }

  if (!user) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-[var(--color-text-muted)]">
        A carregar…
      </div>
    )
  }

  const wsHome = workspace ? `/${workspace.slug}/` : '/'

  return (
    <div className="flex h-full items-center justify-center bg-[var(--color-bg)] text-[var(--color-text)]">
      <div className="w-full max-w-sm rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-8 shadow-lg">
        <div className="flex items-center justify-between text-xs">
          <Link
            to={wsHome}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            ← Voltar
          </Link>
          <span className="text-[var(--color-text-dim)]">{user.email}</span>
        </div>
        <h1 className="mt-4 text-lg font-semibold tracking-tight">
          {hasPassword ? 'Alterar password' : 'Definir password'}
        </h1>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Mínimo {MIN_LENGTH} caracteres. Ao definir password, podes
          entrar diretamente sem pedir magic link de cada vez.
        </p>
        {user.is_vn2r_admin && (
          <Link
            to="/admin"
            className="mt-3 block rounded border border-[var(--color-border)] px-3 py-2 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            Gerir membros e acessos →
          </Link>
        )}

        <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
          {hasPassword && (
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                Password atual
              </span>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]"
              />
            </label>
          )}
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Nova password
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Confirmar nova password
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]"
            />
          </label>

          {status === 'done' && (
            <div className="rounded border border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] p-2 text-xs text-[var(--color-accent)]">
              Password atualizada. Da próxima vez podes entrar
              diretamente em "Password".
            </div>
          )}
          {errorMsg && (
            <div className="text-xs text-red-400">{errorMsg}</div>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={status === 'submitting' || status === 'loading'}
              className="flex-1 rounded bg-[var(--color-accent)] px-3 py-2 text-sm font-medium text-[var(--color-bg)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status === 'submitting'
                ? 'A guardar…'
                : hasPassword
                  ? 'Alterar password'
                  : 'Definir password'}
            </button>
            {status === 'done' && (
              <button
                type="button"
                onClick={() => navigate(wsHome)}
                className="rounded border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                Voltar
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default SetPasswordPage
